import {
  TrackingPageSettings,
  TrackingPageTrackingDetailsBlock,
} from "@redotech/order-tracking-common/tracking-page-definition";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentOrder } from "@redotech/redo-fulfillment-common/fulfillment";
import { ScreenSize } from "@redotech/redo-marketing-templates-common/email-builder";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { pickUpConfig } from "../../../settings-elements/order/pick-up-config-util";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../../context/tracking-page-context";
import { BlockWrapper } from "../shared/block-wrapper";
import {
  getSelectedShipmentWithFallback,
  getShipmentIdFromLocation,
  getShipmentIndex,
} from "../shared/fulfillment-utils";
import { CustomPickupBlock } from "./custom-pickup";
import { getPreviewOrder, getPreviewShipment } from "./preview-mock-data";
import { TrackingDetailsDesktop } from "./tracking-details-desktop";
import { TrackingDetailsMobile } from "./tracking-details-mobile";
import { shouldHidePosTrackingDetails } from "./tracking-details-pos-utils";
import {
  isDelayedDelivery,
  type StatusLabelOverrides,
} from "./tracking-details-shared";
import type { TrackingMapStyle } from "./tracking-map";

// In the customer portal there's no `screenSize` from context, so fall back
// to the historical window-width threshold that the rest of this block uses.
// In the builder we honor the explicit screen-size toggle so the iframe-style
// mobile preview actually exercises the mobile layout.
function shouldRenderMobile(
  environment: TrackingPageEnvironment,
  screenSize: ScreenSize | undefined,
) {
  if (environment === TrackingPageEnvironment.BUILDER) {
    return screenSize === ScreenSize.MOBILE;
  }
  return typeof window !== "undefined" && window.innerWidth <= 1000;
}

export const TrackingDetails = memo(function TrackingDetails({
  title,
  optionalSubtext,
  showMap,
  mapPinColor,
  mapPinShape,
  mapFontColor,
  mapFontFamily,
  mapHeightPx,
  mapZoom,
  statusLabelOverrides,
  hideDeliveryEstimateWhenUnknown,
  hideTrackingNumber,
}: TrackingPageTrackingDetailsBlock) {
  const {
    order,
    environment,
    fulfillmentOrders,
    pageSettings,
    stalledDays,
    textRules,
    screenSize,
  } = useRequiredContext(TrackingPageContext);

  const { fulfillmentIndex: paramsFulfillmentIndex } = useParams<{
    fulfillmentIndex?: string;
  }>();

  const isBuilder = environment === TrackingPageEnvironment.BUILDER;

  // In the page builder we substitute realistic mock data when the merchant
  // hasn't supplied a sample order — and when the supplied shipment lacks a
  // timeline / ETA / tracking code. This way the live block renders the same
  // way it does for a real customer, instead of collapsing to a bare "Status".
  const previewOrder = useMemo(
    () => (isBuilder && !order ? getPreviewOrder() : order),
    [isBuilder, order],
  );

  const shipmentIndex = getShipmentIndex(
    paramsFulfillmentIndex,
    environment,
    previewOrder,
    getShipmentIdFromLocation(),
  );
  const selectedShipment = getSelectedShipmentWithFallback(
    previewOrder?.shipments,
    shipmentIndex,
  );

  const previewShipment = useMemo(() => {
    if (!isBuilder) {
      return selectedShipment;
    }
    if (!selectedShipment || selectedShipment.trackingEvents.length === 0) {
      return getPreviewShipment();
    }
    return selectedShipment;
  }, [isBuilder, selectedShipment]);

  const robustShipment: TrackingPageShipment | undefined = useMemo(() => {
    if (!previewShipment) {
      return undefined;
    }
    if (isDelayedDelivery(previewShipment)) {
      return { ...previewShipment, estimatedDeliveryAt: null };
    }
    return previewShipment;
  }, [previewShipment]);

  const mapStyle = useMemo(
    () => ({
      pinColor: mapPinColor,
      pinShape: mapPinShape,
      fontColor: mapFontColor,
      fontFamily: mapFontFamily,
      heightPx: mapHeightPx,
      zoom: mapZoom,
    }),
    [
      mapPinColor,
      mapPinShape,
      mapFontColor,
      mapFontFamily,
      mapHeightPx,
      mapZoom,
    ],
  );

  return (
    <BlockWrapper
      environment={environment}
      loading={!previewOrder}
      pageSettings={pageSettings}
      withBorder
    >
      {previewOrder && (
        <TrackingDetailsForShipment
          fulfillmentOrders={fulfillmentOrders}
          hideDeliveryEstimateWhenUnknown={hideDeliveryEstimateWhenUnknown}
          hideTrackingNumber={hideTrackingNumber}
          mapStyle={mapStyle}
          optionalSubtext={optionalSubtext}
          order={previewOrder}
          pageSettings={pageSettings}
          screenSize={screenSize}
          shipment={robustShipment}
          showMap={showMap}
          stalledDays={stalledDays}
          statusLabelOverrides={statusLabelOverrides}
          textRules={textRules}
          title={title}
        />
      )}
    </BlockWrapper>
  );
});

const TrackingDetailsForShipment = memo(function TrackingDetailsForShipment({
  shipment,
  order,
  fulfillmentOrders,
  pageSettings,
  optionalSubtext,
  stalledDays,
  screenSize,
  title,
  showMap,
  mapStyle,
  statusLabelOverrides,
  hideDeliveryEstimateWhenUnknown,
  textRules,
  hideTrackingNumber,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  fulfillmentOrders: FulfillmentOrder[];
  pageSettings: TrackingPageSettings | undefined;
  optionalSubtext: string | undefined;
  stalledDays: number | null;
  screenSize: ScreenSize | undefined;
  title: string | undefined;
  showMap: boolean | undefined;
  mapStyle: TrackingMapStyle;
  statusLabelOverrides: StatusLabelOverrides | undefined;
  hideDeliveryEstimateWhenUnknown: boolean | undefined;
  textRules: readonly TrackingTextRule[] | undefined;
  hideTrackingNumber: boolean | undefined;
}) {
  const { returnAppSettings, environment } =
    useRequiredContext(TrackingPageContext);

  const pickupConfig = pickUpConfig(fulfillmentOrders);

  if (shouldHidePosTrackingDetails(order, shipment)) {
    return null;
  }

  const shouldShowAndarPickup =
    returnAppSettings.storeUrl === "andar-wallets.myshopify.com" &&
    shipment &&
    shipment.carrier === "Self-Pickup (by PDQ)";
  const shouldShowIvyCityPickup =
    returnAppSettings.storeUrl === "ivy-city-co.myshopify.com" &&
    pickupConfig.isPickUpOrder;
  if (shouldShowAndarPickup || shouldShowIvyCityPickup) {
    return (
      <CustomPickupBlock
        isPickUpOrder={pickupConfig.isPickUpOrder}
        pageSettings={pageSettings}
        shipment={shipment}
        storeUrl={returnAppSettings.storeUrl}
      />
    );
  }

  if (shouldRenderMobile(environment, screenSize)) {
    return (
      <TrackingDetailsMobile
        hideDeliveryEstimateWhenUnknown={hideDeliveryEstimateWhenUnknown}
        hideTrackingNumber={hideTrackingNumber}
        mapStyle={mapStyle}
        optionalSubtext={optionalSubtext}
        order={order}
        pickupConfig={pickupConfig}
        shipment={shipment}
        showMap={showMap}
        stalledDays={stalledDays}
        statusLabelOverrides={statusLabelOverrides}
        textRules={textRules}
        title={title}
      />
    );
  }

  return (
    <TrackingDetailsDesktop
      hideDeliveryEstimateWhenUnknown={hideDeliveryEstimateWhenUnknown}
      hideTrackingNumber={hideTrackingNumber}
      mapStyle={mapStyle}
      optionalSubtext={optionalSubtext}
      order={order}
      pickupConfig={pickupConfig}
      shipment={shipment}
      showMap={showMap}
      stalledDays={stalledDays}
      statusLabelOverrides={statusLabelOverrides}
      textRules={textRules}
      title={title}
    />
  );
});
